import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { firebaseAuth, firestore, isFirebaseConfigured } from "@/lib/firebase";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

const AuthContext = createContext(null);
async function buildFirebaseUser(firebaseUser) {
  if (!firebaseUser) return null;
  const userRef = doc(firestore, "users", firebaseUser.uid);
  const profile = await getDoc(userRef);
  const profileData = profile.exists() ? profile.data() : {};
  const premiumExpiry = profileData.premiumExpiry?.toDate
    ? profileData.premiumExpiry.toDate().toISOString()
    : profileData.premium_expires_at || null;
  const hasActivePlan = profileData.plan === "premium" && profileData.subscriptionStatus === "active";
  const userData = {
    user_id: firebaseUser.uid,
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    name: profileData.name || firebaseUser.displayName || firebaseUser.email,
    picture: profileData.profilePhotoUrl || firebaseUser.photoURL || null,
    role: profileData.role || "user",
    premium: Boolean(profileData.premium) || hasActivePlan,
    premium_expires_at: premiumExpiry,
    purchases: profileData.purchases || {},
    auth_provider: firebaseUser.providerData?.[0]?.providerId === "google.com" ? "google" : "firebase",
    emailVerified: firebaseUser.emailVerified || false,
  };
  if (!profile.exists()) {
    await setDoc(userRef, {
      uid: firebaseUser.uid,
      email: userData.email,
      name: userData.name,
      role: "user",
      premium: false,
      plan: "free",
      subscriptionStatus: "none",
      premiumStart: null,
      premiumExpiry: null,
      activeDevices: [],
      maxDevices: 2,
      profilePhotoUrl: userData.picture,
      purchases: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await setDoc(userRef, {
      uid: firebaseUser.uid,
      email: userData.email,
      name: userData.name,
      profilePhotoUrl: userData.picture,
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }
  return userData;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check during OAuth callback so AuthCallback can establish session first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (isFirebaseConfigured) {
      const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
        try {
          setUser(await buildFirebaseUser(firebaseUser));
        } finally {
          setLoading(false);
        }
      });
      return unsubscribe;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, captchaToken = "") => {
    if (!isFirebaseConfigured) {
      throw new Error("Firebase is not configured");
    }
    const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
    const userData = await buildFirebaseUser(credential.user);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async () => {
    if (!isFirebaseConfigured) {
      throw new Error("Firebase is not configured");
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const credential = await signInWithPopup(firebaseAuth, provider);
    const userData = await buildFirebaseUser(credential.user);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, captchaToken = "") => {
    if (!isFirebaseConfigured) {
      throw new Error("Firebase is not configured");
    }
    const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
    await updateProfile(credential.user, { displayName: name });

    // Send email verification immediately after registration
    try {
      await sendEmailVerification(credential.user, {
        url: window.location.origin + "/dashboard",
        handleCodeInApp: false,
      });
    } catch (err) {
      console.warn("Failed to send verification email:", err.message);
    }

    await setDoc(doc(firestore, "users", credential.user.uid), {
      uid: credential.user.uid,
      email: credential.user.email,
      name,
      role: "user",
      premium: false,
      plan: "free",
      subscriptionStatus: "none",
      premiumStart: null,
      premiumExpiry: null,
      activeDevices: [],
      maxDevices: 2,
      profilePhotoUrl: null,
      purchases: {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    const userData = await buildFirebaseUser(credential.user);
    setUser(userData);
    return userData;
  };

  const resetPassword = async (email) => {
    if (!isFirebaseConfigured) {
      throw new Error("Firebase is not configured");
    }
    await sendPasswordResetEmail(firebaseAuth, email);
  };

  const logout = async () => {
    if (isFirebaseConfigured) {
      await signOut(firebaseAuth);
      setUser(null);
      return;
    }
    setUser(null);
  };

  const setAuthFromGoogle = (userData) => {
    setUser(userData);
  };

  // Resend verification email to the current user
  const sendVerificationEmail = async () => {
    if (!isFirebaseConfigured || !firebaseAuth.currentUser) {
      throw new Error("No user logged in");
    }
    await sendEmailVerification(firebaseAuth.currentUser, {
      url: window.location.origin + "/dashboard",
      handleCodeInApp: false,
    });
  };

  // Reload the current Firebase user to get fresh emailVerified status
  const reloadUser = async () => {
    if (!isFirebaseConfigured || !firebaseAuth.currentUser) return;
    await firebaseAuth.currentUser.reload();
    const refreshedUser = await buildFirebaseUser(firebaseAuth.currentUser);
    setUser(refreshedUser);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginWithGoogle, register, resetPassword, logout,
      checkAuth, setAuthFromGoogle, sendVerificationEmail, reloadUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
